<template>
  <div class="lesson-list-box relative">
    <VLessonPlaceholder v-if="isEmptyBlock" />
    
    <div v-else class="w-full h-full m-0 box-border transition-all relative"
         :style="[
          CssStyleHelpers.background(
            fields.mainGroup?.backgroundColor,
            fields.mainGroup?.isShowBackgroundColor
          ),
          CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius),
          CssStyleHelpers.margins(fields.mainGroup?.margins),
        ]">
      <div class="w-full h-full flex flex-col">
        <!-- Медиа контент -->
        <div v-if="fields.mainGroup?.isShowBackgroundMedia"
             class="media-background w-full overflow-hidden"
             :style="[
              CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius),
            ]">
          <MediaCard fill
                     :media-type="fields.mainGroup?.backgroundMedia?.type"
                     :image-props="CssImageHelpers.imageProps(fields.mainGroup?.backgroundMedia)"
                     :video-props="CssVideoHelpers.videoProps(fields.mainGroup?.backgroundMedia)"
                     :mask-props="fields.mainGroup?.backgroundMediaMask"
                     :object-fit="fields.mainGroup?.objectFit || 'cover'" />
        </div>

        <!-- Заголовок -->
        <div v-if="fields.titleGroup?.isShown && fields.titleGroup?.title"
             class="w-full pb-0">
          <TextCard
            class="lesson-title"
            :style="[
              CssStyleHelpers.margins({
                bottom: fields.titleGroup?.spacing,
              }),
            ]"
            :text="fields.titleGroup?.title || ''"
          />
        </div>

        <!-- Список -->
        <div class="w-full h-full flex-1 pt-0 pb-0">
          <ul 
            class="list-container"
            :style="[
              CssStyleHelpers.paddings(fields.listGroup?.listItemSpacing, 'padding'),
            ]"
          >
          <li
              v-for="(listItem, index) in fields.listGroup?.listItems"
              :key="index"
              class="list-item"
              :style="[
                CssStyleHelpers.background(
                  fields.listGroup?.listItemBackgroundColor,
                  fields.listGroup?.isShowListItemBackground
                ),
                CssStyleHelpers.borderRadius(fields.listGroup?.listItemsBorderRadius),
                index !== (fields.listGroup?.listItems?.length || 0) - 1 
                  ? { marginBottom: `${fields.listGroup?.listItemsBetweenSpacing || 0}px` }
                  : {}
              ]"
            >
              <!-- Нумерация -->
              <NumberingListItem
                v-if="markerCategory === 'numbering'"
                :text="listItem?.textField?.text || ''"
                :title="listItem?.titleField?.text || ''"
                :show-title="Boolean(listItem?.titleToggleField?.value && listItem?.titleField?.text)"
                :index="index"
                :marker-type="fields.listGroup?.markerType"
                :marker-backgroud-color="fields.listGroup?.markerBackgroudColor"
                :marker-text-color="fields.listGroup?.markerTextColor"
                :is-use-custom-marker-color="fields.listGroup?.isUseCustomMarkerColor"
                :marker-text-spacing="fields.listGroup?.markerTextSpacing"
                :marker-vertical-offset="fields.listGroup?.markerVerticalOffset"
                :start-index="fields.listGroup?.numberingStartIndex"
              />

              <!-- Чекбоксы -->
              <CheckListItem
                v-else-if="markerCategory === 'check'"
                :text="listItem?.textField?.text || ''"
                :title="listItem?.titleField?.text || ''"
                :show-title="Boolean(listItem?.titleToggleField?.value && listItem?.titleField?.text)"
                :index="index"
                :marker-type="fields.listGroup?.markerType"
                :marker-backgroud-color="fields.listGroup?.markerBackgroudColor"
                :marker-text-color="fields.listGroup?.markerTextColor"
                :is-use-custom-marker-color="fields.listGroup?.isUseCustomMarkerColor"
                :marker-text-spacing="fields.listGroup?.markerTextSpacing"
                :marker-vertical-offset="fields.listGroup?.markerVerticalOffset"
              />
              
              <!-- Обычные маркеры -->
              <MarkerListItem
                v-else
                :text="listItem?.textField?.text || ''"
                :title="listItem?.titleField?.text || ''"
                :show-title="Boolean(listItem?.titleToggleField?.value && listItem?.titleField?.text)"
                :index="index"
                :marker-type="fields.listGroup?.markerType"
                :marker-backgroud-color="fields.listGroup?.markerBackgroudColor"
                :marker-text-color="fields.listGroup?.markerTextColor"
                :is-use-custom-marker-color="fields.listGroup?.isUseCustomMarkerColor"
                :marker-text-spacing="fields.listGroup?.markerTextSpacing"
                :marker-vertical-offset="fields.listGroup?.markerVerticalOffset"
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, PropType} from "vue";
import MediaCard from "../cards/MediaCard.vue";
import {ILayoutComponentData} from "../types/ILayoutFieldData";
import {CssImageHelpers, CssStyleHelpers, CssVideoHelpers} from "../utils/cssStyleHelpers";
import {useDataFields} from "../composables/data";
import NumberingListItem from "./list-items/NumberingListItem.vue";
import CheckListItem from "./list-items/CheckListItem.vue";
import MarkerListItem from "./list-items/MarkerListItem.vue";
import { useMarkerTypes } from '../composables/markers';
import TextCard from "../cards/TextCard.vue";
import VLessonPlaceholder from "../VLessonPlaceholder.vue";
const props = defineProps({
  modelValue: {
    type: Object as PropType<ILayoutComponentData | undefined>,
    required: true,
  },
  mode: {
    type: String as PropType<"view" | "edit">,
    default: "view",
  },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: ILayoutComponentData): void
}>();

// Используем композабл для работы с маркерами
const {
  getMarkerCategory,
} = useMarkerTypes();

// Получаем категорию маркера из поля
const markerCategory = computed(() => {
  const newData = props.modelValue?.data;
  if (!newData) return 'numbering';
  
  const { getData } = useDataFields(newData);
  const markerType = getData("listGroup.markerType")?.value || 'numbering-1';
  return getMarkerCategory(markerType);
});

// Використовуємо дані напряму з newData
const applyDefaultMainMargins = (margins?: Record<string, number>) => ({
  top: margins?.top ?? 50,
  right: margins?.right ?? 0,
  bottom: margins?.bottom ?? 0,
  left: margins?.left ?? 0,
  ...margins,
});

const fields = computed(() => {
  const newData = props.modelValue?.data;
  if (!newData) return {titleGroup: {}, listGroup: {}, mainGroup: {}};

  const {getData} = useDataFields(newData);

  return {
    titleGroup: {
      title: getData("titleGroup.title")?.text,
      isShown: getData("titleGroup.isShown")?.value,
      spacing: getData("titleGroup.spacing")?.value || 0,
    },
    mainGroup: {
      isShowBackgroundColor: getData("mainGroup.isShowBackgroundColor")?.value,
      isShowBackgroundMedia: getData("mainGroup.isShowBackgroundMedia")?.value,
      backgroundColor: getData("mainGroup.backgroundColor")?.value,
      borderRadius: getData("mainGroup.borderRadius")?.value,
      backgroundMedia: getData("mainGroup.backgroundMedia"),
      objectFit: getData("mainGroup.backgroundMedia")?.objectFit ?? 'contain',
      backgroundMediaMask: getData("mainGroup.backgroundMediaMask"),
      margins: applyDefaultMainMargins(getData("mainGroup.margins")),
    },
    // Все данные и стили для списка
    listGroup: {
      // Основные данные элементов списка
      listItems: getData("listGroup.listItems")?.listCollection || [],

      // Стили контейнера списка
      listItemSpacing: getData("listGroup.listItemSpacing"),
      listItemsBetweenSpacing: getData("listGroup.listItemsBetweenSpacing")?.value ?? 20,
      listItemsBorderRadius: getData("listGroup.listItemsBorderRadius")?.value,
      markerTextSpacing: getData("listGroup.markerTextSpacing")?.value ?? 20,
      markerVerticalOffset: getData("listGroup.markerVerticalOffset")?.value ?? 0,
      numberingStartIndex: getData("listGroup.numberingStartIndex")?.value ?? 1,

      // Стили маркеров
      markerType: getData("listGroup.markerType")?.value || 'bullet',
      markerBackgroudColor: getData("listGroup.markerBackgroudColor")?.value,
      markerTextColor: getData("listGroup.markerTextColor")?.value,
      isUseCustomMarkerColor: getData("listGroup.isUseCustomMarkerColor")?.value || false,
      
      // Стили элементов списка
      isShowListItemBackground: getData("listGroup.isShowListItemBackground")?.value,
      listItemBackgroundColor: getData("listGroup.listItemBackgroundColor")?.value,
    },
  };
});

const isEditMode = computed(() => props.mode === "edit");

// Перевіряємо чи блок пустий
const isEmptyBlock = computed(() => {
  const hasListItems = fields.value?.listGroup?.listItems && fields.value.listGroup.listItems.length > 0;
  const hasTitle = fields.value?.titleGroup?.isShown && fields.value?.titleGroup?.title && fields.value.titleGroup.title.trim() !== '';
  // Проверяем медиа - должно быть включено и иметь данные
  const hasMedia = fields.value?.mainGroup?.isShowBackgroundMedia && fields.value?.mainGroup?.backgroundMedia && (
    fields.value.mainGroup.backgroundMedia.previewUrl || 
    fields.value.mainGroup.backgroundMedia.url ||
    fields.value.mainGroup.backgroundMedia.type
  );
  const hasColor = fields.value?.mainGroup?.isShowBackgroundColor && fields.value?.mainGroup?.backgroundColor;
  
  return !(hasListItems || hasTitle || hasMedia || hasColor);
});

</script>

<style scoped>
@reference "tailwindcss";
/* Основные стили для контейнера */
.lesson-list-box {
  @apply w-full flex flex-col gap-0;
  container-type: inline-size;
}

/* Стили для медиа фона */
.media-background {
  @apply w-full h-full absolute inset-0 z-0;
  border-radius: inherit;
  overflow: hidden;
}

.media-background :deep(.media-card) {
  height: 100%;
}

/* Override @container height:auto !important from MediaContainerLayer and MaskLayer —
   those resets are correct for inline media but must not apply to the background fill. */
.media-background :deep(.media-container-layer),
.media-background :deep(.media-container-layer > div) {
  height: 100% !important;
  min-height: unset !important;
  max-height: unset !important;
  aspect-ratio: unset !important;
}

.media-background :deep(img),
.media-background :deep(video) {
  @apply w-full h-full object-cover;
}

/* Стили для списка */
.list-container {
  @apply list-none p-0 m-0;
}

.list-item {
  @apply pb-0 rounded-lg transition-all duration-200;
}

.list-item:hover {
  @apply shadow-sm;
}
</style>

